import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Input, InputRef, Card } from 'antd';
import moment from 'moment';
import { RequiredOTPResponse } from '@bringg/types';

import Button from 'components/button/button';

import styles from './two-factor-authentication.module.scss';

export interface LoginResponse {
	success: boolean;
	data: any;
}

export interface TwoFactorAuthenticationProps {
	expirationTime: string | Date;
	email: string;
	getTranslations: (email: string, minutesTillExpired: number) => Record<string, string>;
	onVerify: (code: string) => Promise<LoginResponse | RequiredOTPResponse>;
	onResend: () => Promise<RequiredOTPResponse>;
}

// helper to compute remaining minutes
const getMinutesTillExpired = (exp: string | Date): number => {
	const expirationMoment = moment(exp);
	const now = moment();

	const remainingMinutes = Math.ceil(expirationMoment.diff(now, 'seconds') / 60);

	return Math.max(0, remainingMinutes);
};

const TFA_CODE_LENGTH = 6;
const LAST_INPUT_INDEX = TFA_CODE_LENGTH - 1;
const EMPTY_CODE = Array(TFA_CODE_LENGTH).fill('');

const TwoFactorAuthentication: React.FC<TwoFactorAuthenticationProps> = ({
	expirationTime,
	email,
	onVerify,
	onResend,
	getTranslations
}) => {
	const [code, setCode] = useState<string[]>(EMPTY_CODE);
	const [minutesTillExpired, setMinutesTillExpired] = useState<number>(() => getMinutesTillExpired(expirationTime));
	const [isVerificationFailed, setIsVerificationFailed] = useState<boolean>(false);
	const [isVerifying, setIsVerifying] = useState<boolean>(false);
	const [isResending, setIsResending] = useState<boolean>(false);
	const inputRefs = useRef<Array<InputRef | null>>([]);
	const isMountedRef = useRef<boolean>(false);
	const codeExpired = minutesTillExpired < 1;
	const disabled = isVerifying || codeExpired;

	const translations = useMemo(() => {
		return getTranslations(email, minutesTillExpired);
	}, [email, minutesTillExpired, getTranslations]);

	useEffect(() => {
		setMinutesTillExpired(getMinutesTillExpired(expirationTime));
		const interval = setInterval(() => setMinutesTillExpired(getMinutesTillExpired(expirationTime)), 60000);
		return () => {
			clearInterval(interval);
		};
	}, [expirationTime]);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		focusInput(0);
	}, []);

	const resetInputs = () => {
		setCode(EMPTY_CODE);
		focusInput(0);
	};

	const verifyCode = async (completeCode: string) => {
		try {
			setIsVerifying(true);
			const response = await onVerify(completeCode);

			if (!isMountedRef.current) return;

			if (response.success) {
				setIsVerificationFailed(false);
				return;
			}

			setIsVerificationFailed(true);
			resetInputs();
		} catch (error) {
			if (!isMountedRef.current) return;

			setIsVerificationFailed(true);
			resetInputs();
		} finally {
			if (isMountedRef.current) {
				setIsVerifying(false);
			}
		}
	};

	// Trigger verification when code is complete
	useEffect(() => {
		const completeCode = code.join('');
		const isCodeComplete = completeCode.length === TFA_CODE_LENGTH && code.every(digit => digit !== '');

		if (isCodeComplete && !disabled) {
			verifyCode(completeCode);
		}
	}, [code, disabled]);

	const focusInput = (index: number) => {
		requestAnimationFrame(() => {
			inputRefs.current[index]?.focus();
		});
	};

	const moveToNextInput = (currentIndex: number) => {
		if (currentIndex < TFA_CODE_LENGTH - 1) {
			focusInput(currentIndex + 1);
		}
	};

	const moveToPreviousInput = (currentIndex: number) => {
		if (currentIndex > 0) {
			focusInput(currentIndex - 1);
		}
	};

	const handleChange = (inputIdx: number, value: string) => {
		if (disabled) return;

		// Only allow digits
		if (!/^[0-9]*$/.test(value)) return;

		const newCode = [...code];

		// Handle single character input
		newCode[inputIdx] = value.slice(-1); // Take only the last character
		setCode(newCode);

		// Move to next input if value was entered
		if (inputIdx < LAST_INPUT_INDEX) {
			moveToNextInput(inputIdx);
		}
	};

	const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace') {
			if (code[idx] === '') {
				// If current input is empty, move to previous and clear it
				if (idx > 0) {
					const newCode = [...code];
					newCode[idx - 1] = '';
					setCode(newCode);
					moveToPreviousInput(idx);
				}
			} else {
				// Clear current input
				const newCode = [...code];
				newCode[idx] = '';
				setCode(newCode);
			}
			e.preventDefault();
		} else if (e.key === 'Delete') {
			const newCode = [...code];
			newCode[idx] = '';
			setCode(newCode);
			e.preventDefault();
		} else if (e.key === 'ArrowLeft' && idx > 0) {
			moveToPreviousInput(idx);
			e.preventDefault();
		} else if (e.key === 'ArrowRight' && idx < LAST_INPUT_INDEX) {
			moveToNextInput(idx);
			e.preventDefault();
		} else if (/[0-9]/.test(e.key)) {
			// Clear the current input before the onChange handler processes the new value
			// This ensures that when clicking on a filled input and typing, it replaces the value
			const newCode = [...code];
			newCode[idx] = '';
			setCode(newCode);
		}
	};

	const handleClick = (idx: number, e: React.MouseEvent<HTMLInputElement>) => {
		// Select all text when clicking on an input with a value
		// This makes it easier to replace the digit
		if (code[idx]) {
			(e.target as HTMLInputElement).select();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
		if (disabled) return;

		e.preventDefault();
		const pastedData = e.clipboardData.getData('text');
		const digits = pastedData.replace(/\D/g, '').slice(0, TFA_CODE_LENGTH);

		if (digits.length !== TFA_CODE_LENGTH) {
			return;
		}

		setCode(Array.from(digits));
		focusInput(LAST_INPUT_INDEX);
	};

	const handleResendClick = async () => {
		try {
			setIsResending(true);
			const response = await onResend();

			if (!isMountedRef.current) return;
			if (response.expiration) {
				setMinutesTillExpired(getMinutesTillExpired(response.expiration));
			}

			setCode(EMPTY_CODE);
			setIsVerificationFailed(false);
			focusInput(0);
		} catch (error) {
			console.error('Failed to resend code:', error);
		} finally {
			if (isMountedRef.current) {
				setIsResending(false);
			}
		}
	};

	return (
		<div className={`${styles.twoFactorAuthentication} login-page-wrapper`} onPasteCapture={handlePaste}>
			<Card>
				<div className={styles.loginHeader}>
					<span className={styles.loginTitle}>{translations?.title}</span>
				</div>
				<div className={styles.loginBody}>
					<div className={styles.emailVerification}>
						<div className={styles.description}>
							<span>{translations?.description}</span>
							<span>{translations?.enterCode}</span>
							<span>{translations?.verificationCode}</span>
						</div>
						<div className={styles.verificationCodeInputs}>
							{code.map((value, idx) => (
								<div
									key={`2fa-digit-${idx}`}
									className={`${styles.codeInputContainer} ${isVerificationFailed ? 'invalid' : ''}`}
								>
									<Input
										type="text"
										id={`2fa-digit-${idx}`}
										value={value}
										onChange={e => handleChange(idx, e.target.value)}
										onKeyDown={e => handleKeyDown(idx, e)}
										onClick={e => handleClick(idx, e)}
										ref={el => (inputRefs.current[idx] = el)}
										maxLength={1}
										disabled={disabled}
										autoComplete="off"
										inputMode="numeric"
									/>
								</div>
							))}
						</div>

						{isVerificationFailed && (
							<span className={styles.otpValidationMessage}>{translations?.incorrectCode}</span>
						)}

						{codeExpired ? (
							<p className={styles.notificationMessage}>{translations?.codeIsExpired}</p>
						) : (
							<p className={styles.notificationMessage}>{translations?.expirationTime}</p>
						)}

						<div className={styles.resend}>
							<span className={styles.resendMessage}>{translations?.resendEmail}</span>
							<Button
								type="inline-link"
								disabled={isResending || !codeExpired}
								onClick={handleResendClick}
							>
								{translations?.resendCodeButton}
							</Button>
						</div>
					</div>
				</div>
			</Card>
		</div>
	);
};

export default TwoFactorAuthentication;
